// MapTile contains all the layers of a tile on the map. The actual
// tile picture being drawn is handled in the other tile components
// such as GearTile, and MapTile uses these as well as TileWalls and
// stars and robots to create a full tile.

import { useCopyTileContext } from 'app/src/editor/CopyTileContext';
import { useMapStoreContext } from 'app/src/editor/MapStoreContext';
import { usePluginsContext } from 'app/src/editor/PluginsContext';
import { useTileHoverContext } from 'app/src/editor/TileHoverContext';
import {
	TileInteractionMode,
	useTileInteractionModeContext,
} from 'app/src/editor/TileInteractionModeContext';
import TileManipulationHelper from 'app/src/editor/TileManipulationHelper';
import { useUndoRedoContext } from 'app/src/editor/UndoRedoContext';
import {
	getTurnsFromOrientation,
	MovableItemOrientation,
} from 'app/src/engine/movable-items/MovableItem';
import Tile from 'app/src/engine/tiles/Tile';
import { TileWallType } from 'app/src/engine/tiles/TileWalls';
import TileWalls from 'app/src/game/map/tiles/TileWalls';
import * as key from 'keymaster';
import * as React from 'react';

enum HoverQuadrant {
	Top,
	Right,
	Bottom,
	Left,
}

function getWallForHoverQuadrant(quadrant: HoverQuadrant): TileWallType {
	switch (quadrant) {
		case HoverQuadrant.Top:
			return TileWallType.Top;
		case HoverQuadrant.Right:
			return TileWallType.Right;
		case HoverQuadrant.Bottom:
			return TileWallType.Bottom;
		case HoverQuadrant.Left:
			return TileWallType.Left;
	}
}

function getHoverQuadrantFromCoords(
	x: number,
	y: number,
	containerWidth: number,
	containerHeight: number,
): HoverQuadrant {
	const slope = y / x;
	const inverseSlope = (containerHeight - y) / x;
	const containerRatio = containerWidth / containerHeight;

	if (slope > containerRatio) {
		if (inverseSlope < containerRatio) {
			return HoverQuadrant.Bottom;
		} else {
			return HoverQuadrant.Left;
		}
	} else {
		if (inverseSlope < containerRatio) {
			return HoverQuadrant.Right;
		} else {
			return HoverQuadrant.Top;
		}
	}
}

interface Props {
	row: number;
	col: number;

	tile: Tile;

	containsStar?: boolean;
	robotOrientation?: MovableItemOrientation | null;

	children: React.ReactNode;
}

export default function MapTile({
	row,
	col,
	tile,
	containsStar,
	robotOrientation,
	children,
}: Props): React.ReactElement {
	const tileContainerRef = React.createRef<HTMLDivElement | null>();

	const {
		mode: interactionMode,
		setMode: setIteractionMode,
		tile: interactionModeTile,
		setTile: setInteractionModeTile,
	} = useTileInteractionModeContext();

	const { coords: hoveredTileCoords, setTileCoordinates } = useTileHoverContext();
	const hovered =
		hoveredTileCoords && hoveredTileCoords.row === row && hoveredTileCoords.col === col;

	const [ghostedWall, setGhostedWall] = React.useState<TileWallType | null>(null);
	const visibleGhostedWall =
		hovered && interactionMode === TileInteractionMode.Wall ? ghostedWall : null;

	const { addTile: copyTile } = useCopyTileContext();

	const { mapStore } = useMapStoreContext();

	const { addUndoState } = useUndoRedoContext();

	const plugins = usePluginsContext();

	// Handlers
	const getHoverQuadrant = (
		e: React.MouseEvent<HTMLElement>,
		container: HTMLElement,
	): HoverQuadrant | null => {
		const containerSize = {
			width: container.offsetWidth,
			height: container.offsetHeight,
		};

		// Get the coords of mouse relative to the container.
		const containerRect = container.getBoundingClientRect();
		const coords = {
			x: e.pageX - containerRect.left,
			y: e.pageY - containerRect.top,
		};

		// Have to use any here because TS doesn't understand that
		// type S extends TileState.
		if (
			coords.x < 0 ||
			coords.y < 0 ||
			coords.x > containerSize.width ||
			coords.y > containerSize.height
		) {
			return null;
		} else {
			return getHoverQuadrantFromCoords(
				coords.x,
				coords.y,
				containerSize.width,
				containerSize.height,
			);
		}
	};

	const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
		if (interactionMode === TileInteractionMode.Wall) {
			const quadrant = getHoverQuadrant(e, tileContainerRef.current!);

			if (quadrant !== null) {
				const newGhostedWall = getWallForHoverQuadrant(quadrant);

				// Get the current description of the tile.
				const walls = new Set(tile.walls);

				// Conditionally add or remove the wall.
				if (walls.has(newGhostedWall)) {
					walls.delete(newGhostedWall);
				} else {
					walls.add(newGhostedWall);
				}

				// Create a new description and overwrite walls. We have to cast
				// as "any" to get around the "readonly" flag on walls, but it
				// should be okay since we clone the whole description. Not sure
				// if there's a better way to do this.
				const newTile = tile.clone();
				newTile.walls = walls;

				// Update the description in the map.
				mapStore.updateTile(row, col, newTile);

				// Add an undo state.
				addUndoState();
			}
		} else if (interactionMode === TileInteractionMode.AddTile && interactionModeTile) {
			const interactionModeTileCloned = interactionModeTile.clone();
			mapStore.updateTile(row, col, interactionModeTileCloned);

			// Add an undo state.
			addUndoState();
		} else if (interactionMode === TileInteractionMode.Star) {
			mapStore.toggleStar(row, col);

			// Add an undo state.
			addUndoState();
		} else if (interactionMode === TileInteractionMode.Robot) {
			if (robotOrientation !== undefined && robotOrientation !== null) {
				if (robotOrientation === MovableItemOrientation.Left) {
					mapStore.setRobot(null);
				} else {
					let newOrientation: MovableItemOrientation | null = MovableItemOrientation.Up;

					switch (robotOrientation) {
						case MovableItemOrientation.Up:
							newOrientation = MovableItemOrientation.Right;
							break;
						case MovableItemOrientation.Right:
							newOrientation = MovableItemOrientation.Down;
							break;
						case MovableItemOrientation.Down:
							newOrientation = MovableItemOrientation.Left;
							break;
					}

					mapStore.setRobot({
						row,
						col,
						orientation: newOrientation,
						turns: getTurnsFromOrientation(newOrientation),
					});
				}
			} else {
				mapStore.setRobot({
					row,
					col,
					orientation: MovableItemOrientation.Up,
					turns: getTurnsFromOrientation(MovableItemOrientation.Up),
				});
			}

			// Add an undo state.
			addUndoState();
		} else if (interactionMode === TileInteractionMode.Rotate) {
			// Make a rotated clone.
			const newTile = TileManipulationHelper.getRotatedTile(tile, !key.option);

			// Update the tile in the map.
			mapStore.updateTile(row, col, newTile);

			// Add an undo state.
			addUndoState();
		} else if (interactionMode === TileInteractionMode.Copy) {
			// Make a copy of the tile and strip off the walls.
			const newTile = tile.clone();
			newTile.walls = new Set();

			// Add the tile to the copy archive.
			copyTile(newTile);

			// Switch to the copied tile in tile mode.
			setIteractionMode(TileInteractionMode.AddTile);
			setInteractionModeTile(newTile);
		}
	};

	const handleMouseMoved = (e: React.MouseEvent<HTMLDivElement>): void => {
		if (!hovered) {
			// Set the hover. For details, see TileHoverStore.
			setTileCoordinates({
				row,
				col,
			});
		}

		if (interactionMode === TileInteractionMode.Wall) {
			const quadrant = getHoverQuadrant(e, tileContainerRef.current!);

			if (quadrant !== null) {
				const newGhostedWall = getWallForHoverQuadrant(quadrant);
				setGhostedWall(newGhostedWall);
			}
		}
	};

	const handleMouseLeave = (): void => {
		// Clear the hover. For details, see TileHoverStore.
		setTileCoordinates(null);
	};

	// Render
	let ghostedTile: JSX.Element | null = null;
	if (hovered && interactionMode === TileInteractionMode.AddTile && interactionModeTile) {
		const ghostedTilePlugin = Array.from(plugins).find((p) =>
			p.providesNodeForTile(interactionModeTile),
		);
		const ghostedTileNode =
			(ghostedTilePlugin && ghostedTilePlugin.getTileNode(interactionModeTile, row, col)) || null;
		ghostedTile = <div className="MapTile-ghostedTile">{ghostedTileNode}</div>;
	}

	let star: JSX.Element | null = null;
	if (containsStar) {
		star = <div className="MapTile-star" />;
	} else if (hovered && interactionMode === TileInteractionMode.Star) {
		star = <div className="MapTile-star MapTile-star--ghosted" />;
	}

	let robot: JSX.Element | null = null;
	if (robotOrientation !== undefined && robotOrientation !== null) {
		const robotClassNames = ['MapTile-robot'];

		switch (robotOrientation) {
			case MovableItemOrientation.Up:
				robotClassNames.push('MapTile-robot--up');
				break;
			case MovableItemOrientation.Right:
				robotClassNames.push('MapTile-robot--right');
				break;
			case MovableItemOrientation.Down:
				robotClassNames.push('MapTile-robot--down');
				break;
			case MovableItemOrientation.Left:
				robotClassNames.push('MapTile-robot--left');
				break;
		}

		robot = <div className={robotClassNames.join(' ')} />;
	} else if (hovered && interactionMode === TileInteractionMode.Robot) {
		robot = <div className="MapTile-robot MapTile-robot--ghosted MapTile-robot--up" />;
	}

	return (
		<div
			className="MapTile"
			onClick={handleClick}
			onMouseMove={handleMouseMoved}
			onMouseLeave={handleMouseLeave}
			ref={tileContainerRef as React.RefObject<HTMLDivElement>}
		>
			{children}
			{ghostedTile}
			{star}
			{robot}
			<TileWalls walls={tile.walls} ghostedWall={visibleGhostedWall} />
		</div>
	);
}
